import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as analytics from "@/services/analytics";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { SearchInput } from "./SearchInput";
import { SearchModeToggle } from "./SearchModeToggle";
import { DriveSearchFormInputs, SearchMode } from "./types";
import { SortDropdown, type SortOption } from "@/components/sortDropdown";

export interface Props {
  folderId?: string;
  currentSort?: SortOption;
  onSortChange?: (sort: SortOption) => void;
}

export const DriveSearch: React.FC<Props> = ({ folderId, currentSort, onSortChange }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get("mode") === "ai" ? "ai" : "standard";
  const [searchMode, setSearchMode] = React.useState<SearchMode>(initialMode);

  React.useEffect(() => {
    setSearchMode(searchParams.get("mode") === "ai" ? "ai" : "standard");
  }, [searchParams]);

  const methods = useForm<DriveSearchFormInputs>({
    defaultValues: {
      query: searchParams.get("q") || "",
    },
  });

  const searchQuery = methods.watch("query");

  const updateUrl = React.useCallback(
    (query: string, mode: SearchMode = searchMode) => {
      query = query.trim();
      const newSearchParams = new URLSearchParams(searchParams);
      if (query) {
        newSearchParams.set("q", query);
        newSearchParams.set("mode", mode);
      } else {
        newSearchParams.delete("q");
        newSearchParams.delete("mode");
      }
      const searchQueryString = newSearchParams.toString();
      navigate(pathname + (searchQueryString ? "?" + searchQueryString : ""));

      analytics.searchFilesAndFolders(query, folderId, mode);

      methods.setValue("query", query);
    },
    [navigate, pathname, searchParams, methods, folderId, searchMode],
  );

  const onSubmit = (payload: DriveSearchFormInputs) => {
    if (payload.query) {
      const trimmed = payload.query.trim();
      updateUrl(trimmed, searchMode);
    }
  };

  const handleSearch = () => {
    analytics.search(methods.getValues("query") || "");
  };

  const handleClear = () => {
    updateUrl("");
  };

  return (
    <div
      data-testid="drive-search-toolbar"
      className="mb-6 rounded-xl border border-desert-sand-200 bg-white p-3 shadow-sm"
    >
      <FormProvider {...methods}>
        <form method="post" onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-center">
            <div className="min-w-0 flex-1">
              <SearchInput searchMode={searchMode} />
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2">
              <SearchModeToggle searchMode={searchMode} onModeChange={setSearchMode} />

              <Button type="submit" onClick={handleSearch} variant="coral" size="sm">
                Search
              </Button>

              {searchQuery && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="rounded-md px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-coral"
                >
                  Clear
                </button>
              )}
              {currentSort && onSortChange && (
                <SortDropdown
                  className="mb-0"
                  label=""
                  currentSort={currentSort}
                  onSortChange={onSortChange}
                  fields={[
                    { key: "name", label: "Name" },
                    { key: "created_at", label: "Created date" },
                  ]}
                />
              )}
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
